/**
 * "Link issue" dialog — opened from a failed assertion row in `StepAssertions`
 * when it has no open linked issue yet. Two modes:
 *  - Attach to an open issue already filed against this workflow.
 *  - File a brand-new issue.
 *
 * Both link the issue to this specific failing assertion via
 * `subjectAssertionKey` (+ `runId`), which is the whole "linked issue"
 * mechanism — there is no separate "known issue" concept.
 *
 * @module ui/pages/FlowDesigner/runs/LinkAssertionIssueDialog
 */
import React from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  UnderlineTabs,
  UnderlineTab,
  TextField,
  Select,
  Button,
  Flex,
  Text,
  useToast,
} from '@chatsdet/ui';
import type { OpenWorkflowIssueSummary } from '../../flow-list/types';
import {
  useCreateManualIssueMutation,
  useAddIssueOccurrenceMutation,
} from '../../../../hooks/useManualIssueMutations';
import { toastErrorOptionsFor } from '../../../../lib/toast-error-options';

type Severity = 'high' | 'medium' | 'low' | 'unknown';

const SEVERITY_OPTIONS: { id: Severity; label: string }[] = [
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
  { id: 'unknown', label: 'Unknown' },
];

/** Category used when a human files a manual issue from a failed assertion with no diagnosis. */
const DEFAULT_MANUAL_CATEGORY_KEY = 'business_logic_failure';

export interface LinkAssertionIssueDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  runId: string;
  /** Needed to file a NEW issue; `null` limits the dialog to attaching to an open one. */
  propertyId: string | null;
  /** The failing assertion's `key` — links the filed/attached issue to it. */
  assertionKey: string;
  /** Short assertion label/condition, used to prefill the new-issue title. */
  assertionLabel: string;
  /** Open issues already filed against this workflow — candidates to attach to. */
  openIssues: OpenWorkflowIssueSummary[];
  /** Best-effort URL for the page the assertion failed on (run's target env). */
  primaryUrl?: string | null;
}

export function LinkAssertionIssueDialog({
  isOpen,
  onOpenChange,
  workflowId,
  runId,
  propertyId,
  assertionKey,
  assertionLabel,
  openIssues,
  primaryUrl,
}: LinkAssertionIssueDialogProps) {
  const { toast } = useToast();
  // An issue already linked to a *different* assertion is not a valid attach
  // target — the server rejects it with ISSUE_LINKED_TO_OTHER_ASSERTION.
  const attachCandidates = React.useMemo(
    () =>
      openIssues.filter(
        (issue) => issue.subjectKind !== 'assertion' || issue.subjectAssertionKey === assertionKey
      ),
    [openIssues, assertionKey]
  );
  // No reset effect: this component is mounted fresh (keyed by assertion) each
  // time a "Link issue" click opens it, so useState's initializer is the reset.
  const [mode, setMode] = React.useState<'attach' | 'new'>(
    attachCandidates.length > 0 ? 'attach' : 'new'
  );
  const [selectedIssueId, setSelectedIssueId] = React.useState<string | null>(
    attachCandidates[0]?.id ?? null
  );
  const [title, setTitle] = React.useState(assertionLabel);
  const [severity, setSeverity] = React.useState<Severity>('medium');

  const createIssue = useCreateManualIssueMutation(workflowId);
  const addOccurrence = useAddIssueOccurrenceMutation(workflowId, selectedIssueId);

  const isPending = createIssue.isPending || addOccurrence.isPending;

  const handleSubmit = async () => {
    try {
      if (mode === 'attach') {
        if (!selectedIssueId) return;
        await addOccurrence.mutateAsync({
          runId,
          subjectAssertionKey: assertionKey,
          primaryUrl: primaryUrl ?? null,
          body: null,
          evidence: null,
        });
        toast.success('Issue occurrence added');
      } else {
        if (!title.trim() || propertyId === null) return;
        await createIssue.mutateAsync({
          propertyId,
          issueTitle: title.trim(),
          issueSeverity: severity,
          issueCategoryKey: DEFAULT_MANUAL_CATEGORY_KEY,
          issueCategoryId: null,
          runId,
          subjectAssertionKey: assertionKey,
          primaryUrl: primaryUrl ?? null,
          body: null,
          evidence: null,
        });
        toast.success('Issue filed');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : mode === 'attach'
            ? 'Failed to attach issue'
            : 'Failed to file issue',
        toastErrorOptionsFor(err)
      );
    }
  };

  // The server requires a primary URL for both modes (see
  // `manual-filing-service.ts`'s ISSUE_PRIMARY_URL_REQUIRED).
  const canSubmit =
    Boolean(primaryUrl) &&
    (mode === 'attach' ? Boolean(selectedIssueId) : title.trim().length > 0 && propertyId !== null);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <span />
      <Dialog title="Link issue" description="Track this assertion failure as an open issue.">
        <DialogContent>
          <Flex direction="column" gap="md">
            {attachCandidates.length > 0 && (
              <UnderlineTabs
                selectedId={mode}
                onSelectionChange={(id) => setMode(id === 'new' ? 'new' : 'attach')}
              >
                <UnderlineTab id="attach">Attach to open issue</UnderlineTab>
                <UnderlineTab id="new" isDisabled={propertyId === null}>
                  File new issue
                </UnderlineTab>
              </UnderlineTabs>
            )}

            {mode === 'attach' ? (
              <Select
                label="Open issue"
                items={attachCandidates.map((issue) => ({
                  id: issue.id,
                  label: `${issue.shortcode} — ${issue.title}`,
                }))}
                selectedKey={selectedIssueId}
                onSelectionChange={(key) => setSelectedIssueId(key as string)}
                isDisabled={isPending}
              />
            ) : (
              <>
                <TextField
                  label="Issue title"
                  value={title}
                  onChange={setTitle}
                  isDisabled={isPending}
                  autoFocus
                />
                <Select
                  label="Severity"
                  items={SEVERITY_OPTIONS}
                  selectedKey={severity}
                  onSelectionChange={(key) => setSeverity(key as Severity)}
                  isDisabled={isPending}
                />
              </>
            )}

            {!primaryUrl && (
              <Text size="xs" className="text-amber-600 dark:text-amber-400">
                No target URL available for this run — filing or attaching an issue isn't possible
                until one is.
              </Text>
            )}

            <Text size="xs" className="text-muted-foreground">
              Linked to this run's failing assertion — its next failure will show as covered by
              this issue instead of filing a new one.
            </Text>
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => onOpenChange(false)} isDisabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleSubmit} isDisabled={isPending || !canSubmit}>
            {isPending ? 'Saving…' : mode === 'attach' ? 'Attach' : 'File issue'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
